import { GetDashboardUpcomingSessionModel } from "@factory/model/query-model-dashboard";
import { useQyGetUpcomingSessions } from "@factory/query/dashboard.query";
import { useQyGetUtilCategory } from "@factory/query/util-category.query";
import { LabelValue } from "@shared/models/label-value.interface";
import { TableSort } from "@shared/models/table-sort";
import ErrorSection from "@shared/ui/error-section/error-section";
import HeaderContent from "@shared/ui/header-content/header-content";
import SearchInput from "@shared/ui/search-input/search-input";
import SkeletonList from "@shared/ui/skeleton-list/skeleton-list";
import { format, isValid, parse } from "date-fns";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { DataTableStateEvent } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { Paginator, PaginatorPageChangeEvent } from "primereact/paginator";
import { useMemo, useState } from "react";
import { CiSearch } from "react-icons/ci";
import { FaChevronLeft } from "react-icons/fa";
import { MdClear } from "react-icons/md";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { SESSION_FILTER_PARAMS } from "../upcoming-session.util";
import UpcomingSessionTable from "../upcoming-session-table/upcoming-session-table";

const DAY_FILTER_OPTIONS = [
  { label: "Today", value: "TODAY" },
  { label: "Yesterday", value: "YESTERDAY" },
  { label: "Next 7 days", value: "WEEK" },
  { label: "Next month", value: "MONTH" },
] as LabelValue[];

const DEFAULT_ROW_LIMIT = 10;

const parseQueryDate = (value: string | null) => {
  if (!value) return null;
  const parsed = parse(value, "yyyy-MM-dd", new Date());
  return isValid(parsed) ? parsed : null;
};

export function ListUpcomingSession() {
  const navigate = useNavigate();
  const { client } = useParams<{ client: string }>();
  const [searchParams] = useSearchParams();

  // Start from the filters the dashboard had when "View All" was clicked
  const [dayFilter, setDayFilter] = useState<string>(
    searchParams.get(SESSION_FILTER_PARAMS.dateFilter) ?? "",
  );
  const [dateRange, setDateRange] = useState<(Date | null)[]>(() => {
    const start = parseQueryDate(
      searchParams.get(SESSION_FILTER_PARAMS.startDate),
    );
    const end = parseQueryDate(searchParams.get(SESSION_FILTER_PARAMS.endDate));
    return start && end ? [start, end] : [];
  });
  const [category, setCategory] = useState<string>(
    searchParams.get(SESSION_FILTER_PARAMS.category) ?? "",
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [rowLimit, setRowLimit] = useState(DEFAULT_ROW_LIMIT);
  const [first, setFirst] = useState(0);
  const [tableSort, setTableSort] = useState<TableSort>({
    sortField: undefined,
    sortOrder: undefined,
  });
  const [order, setOrder] = useState<Record<string, string>>({});

  const { data: categoryData } = useQyGetUtilCategory("", 99999);
  const categoryOptions = useMemo(() => {
    if (!categoryData?.data) return [];
    return LabelValue.MapArray(
      categoryData.data,
      (label) => label.name || "-",
      (value) => value.code,
    );
  }, [categoryData]);

  const hasRange = !!dateRange?.[0] && !!dateRange?.[1];
  const startDate = hasRange
    ? format(dateRange[0] as Date, "yyyy-MM-dd")
    : undefined;
  const endDate = hasRange
    ? format(dateRange[1] as Date, "yyyy-MM-dd")
    : undefined;
  const filter = useMemo(
    () => (category ? { filter: JSON.stringify({ category }) } : undefined),
    [category],
  );

  const { data, isLoading, isError, error, isFetching } =
    useQyGetUpcomingSessions(
      searchTerm,
      rowLimit,
      first,
      order,
      filter,
      dayFilter || undefined,
      startDate,
      endDate,
    );

  // Any filter change starts again from the first page
  const resetPaging = () => setFirst(0);

  const handleDayFilterChange = (value: string) => {
    setDayFilter(value ?? "");
    if (value) setDateRange([]);
    resetPaging();
  };
  const handleDateRangeChange = (value: (Date | null)[] | null) => {
    setDateRange(value ?? []);
    if (value?.[0]) setDayFilter("");
    resetPaging();
  };
  const handleCategoryChange = (value: string) => {
    setCategory(value ?? "");
    resetPaging();
  };
  const handleSearch = (term: string) => {
    setSearchTerm(term);
    resetPaging();
  };
  const handleClear = () => {
    setDayFilter("");
    setDateRange([]);
    setCategory("");
    setSearchTerm("");
    resetPaging();
  };
  const handleTableSort = (e: DataTableStateEvent) => {
    setTableSort({ sortField: e.sortField, sortOrder: e.sortOrder });
    setOrder(
      e.sortField ? { [e.sortField]: e.sortOrder === -1 ? "DESC" : "ASC" } : {},
    );
    resetPaging();
  };
  const onPageChange = (event: PaginatorPageChangeEvent) => {
    setFirst(event.first);
    setRowLimit(event.rows);
  };
  const handleRowSelect = (row: GetDashboardUpcomingSessionModel) => {
    navigate(
      `/${client}/visit/${row.visit}?name=${encodeURIComponent(
        row.client_name,
      )}`,
    );
  };

  const filterElement = (
    <section className="flex flex-col gap-4 mb-6">
      <div className="flex justify-between gap-4">
        <Dropdown
          // null (not "") so the clear icon only shows when a value is set
          value={dayFilter || null}
          onChange={(e) => handleDayFilterChange(e.value)}
          options={DAY_FILTER_OPTIONS}
          placeholder="All upcoming"
          className="w-full"
          showClear
          disabled={!!dateRange?.[0]}
        />
        <Calendar
          value={dateRange as any}
          onChange={(e) => handleDateRangeChange(e.value as any)}
          selectionMode="range"
          readOnlyInput
          hideOnRangeSelection
          placeholder="Select date range"
          className="w-full"
          disabled={!!dayFilter}
        />
        <Dropdown
          value={category || null}
          onChange={(e) => handleCategoryChange(e.value)}
          options={categoryOptions}
          placeholder="Select category"
          className="w-full"
          showClear
        />
        <Button
          className="block w-28"
          severity="secondary"
          outlined
          label="Clear"
          onClick={handleClear}
        />
      </div>
      <SearchInput
        clearIcon={<MdClear />}
        searchIcon={<CiSearch />}
        searchTerm={searchTerm}
        onSearch={handleSearch}
        placeholder="Search client or service"
        className="w-full block"
      />
    </section>
  );

  const grid = (
    <section>
      <h5 className="mb-2">
        Total: <b>{data?.count ?? 0}</b>
      </h5>
      <div className={isFetching ? "opacity-60" : ""}>
        <UpcomingSessionTable
          rows={data?.data ?? []}
          startIndex={first}
          sortable
          sortField={tableSort.sortField}
          sortOrder={tableSort.sortOrder}
          onSort={handleTableSort}
          onRowSelect={handleRowSelect}
          emptyMessage="No sessions found for the selected filters."
        />
      </div>
      <Paginator
        first={first}
        rows={rowLimit}
        totalRecords={data?.count ?? 0}
        rowsPerPageOptions={[10, 20, 30]}
        onPageChange={onPageChange}
      />
    </section>
  );

  return (
    <div id="ListUpcomingSession" className="upcoming-session">
      <HeaderContent
        title={<h3>Upcoming Sessions / Schedule</h3>}
        backIcon={<FaChevronLeft />}
        onBack={() => navigate(`/${client}`)}
      />

      <div className="p-7">
        <p className="text-gray-500 mb-4">
          Scheduled client sessions, soonest first. With no date filter, every
          session from today onward is listed.
        </p>
        {filterElement}
        {isLoading && <SkeletonList count={5} />}
        {isError && !isLoading && (
          <ErrorSection
            title="Error Occured"
            message={(error as any)?.message}
          />
        )}
        {!isLoading && !isError && grid}
      </div>
    </div>
  );
}

export default ListUpcomingSession;
